import { categories } from "@/mocks/categories";
import { shehuiArticles } from "@/mocks/articles/shehui";
import { yuleArticles } from "@/mocks/articles/yule";
import { caijingArticles } from "@/mocks/articles/caijing";
import { wangluoArticles } from "@/mocks/articles/wangluo";
import { kejiArticles } from "@/mocks/articles/keji";
import { shenghuoArticles } from "@/mocks/articles/shenghuo";

export interface Article {
  id: string;
  title: string;
  summary: string;
  categoryId: string;
  categoryName: string;
  publishedAt: string;
  source: string;
  tags: string[];
  heat: number;
  image?: string;
  content: string[];
}

export interface Category {
  id: string;
  name: string;
  description: string;
  icon: string;
  image: string;
}

export type SortKey = "time" | "heat";

export const allCategories = categories as Category[];

const rawArticles = [
  ...shehuiArticles,
  ...yuleArticles,
  ...caijingArticles,
  ...wangluoArticles,
  ...kejiArticles,
  ...shenghuoArticles,
] as Article[];

export const allArticles: Article[] = [...rawArticles].sort((a, b) =>
  a.publishedAt < b.publishedAt ? 1 : -1
);

export function getCategoryById(id: string): Category | undefined {
  return allCategories.find((item) => item.id === id);
}

export function resolveCategory(param: string): Category | undefined {
  const decoded = decodeURIComponent(param || "");
  return allCategories.find(
    (item) => item.id === decoded || item.name === decoded
  );
}

export function getArticleById(id: string): Article | undefined {
  return allArticles.find((item) => item.id === id);
}

export function getArticlesByCategory(categoryId: string): Article[] {
  return allArticles.filter((item) => item.categoryId === categoryId);
}

export function sortArticles(list: Article[], key: SortKey): Article[] {
  const next = [...list];
  if (key === "heat") {
    next.sort((a, b) => b.heat - a.heat);
  } else {
    next.sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
  }
  return next;
}

export function searchArticles(keyword: string): Article[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return allArticles.filter((item) => {
    const haystack = [
      item.title,
      item.summary,
      item.categoryName,
      item.source,
      item.tags.join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getHotArticles(limit = 6): Article[] {
  return sortArticles(allArticles, "heat").slice(0, limit);
}

export function getLatestArticles(limit?: number): Article[] {
  const list = sortArticles(allArticles, "time");
  return typeof limit === "number" ? list.slice(0, limit) : list;
}

export function getRelatedArticles(article: Article, limit = 5): Article[] {
  const sameCategory = allArticles.filter(
    (item) => item.id !== article.id && item.categoryId === article.categoryId
  );
  const others = allArticles.filter(
    (item) => item.id !== article.id && item.categoryId !== article.categoryId
  );
  return [...sameCategory, ...others]
    .sort((a, b) => b.heat - a.heat)
    .slice(0, limit);
}

export function paginate<T>(list: T[], page: number, pageSize: number) {
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    total,
    totalPages,
    currentPage: safePage,
  };
}

export function formatDate(value: string): string {
  return value.split(" ")[0] || value;
}

export function getArticleImage(article: Article): string {
  if (article.image) return article.image;
  return getCategoryById(article.categoryId)?.image || "";
}